import React, { useEffect, useId, useRef, useState } from 'react';
import type { PassportVisual } from '@bilt/domain/passportVisual';
/** Decorations never schedule writes. One mounted card owns the finite motion budget. */
export default function PassportMaterial({
  visual,
  quiet,
  face,
}: {
  visual?: PassportVisual;
  quiet: boolean;
  face: 'front' | 'back';
}) {
  const id = useId();
  const element = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [replay, setReplay] = useState(0);
  useEffect(() => {
    if (!visual || quiet || visual.reveal.preset === 'still') return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motion.matches || (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4))
      return;
    const stop = (e: Event) => {
      if ((e as CustomEvent<string>).detail !== id) setPlaying(false);
    };
    const reduce = () => setPlaying(false);
    window.addEventListener('passport-material-active', stop);
    motion.addEventListener('change', reduce);
    const hide = () => { if (document.hidden) setPlaying(false); };
    document.addEventListener('visibilitychange', hide);
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(([entry]) => { if (!entry.isIntersecting) setPlaying(false); });
    if (element.current) observer?.observe(element.current);
    window.dispatchEvent(new CustomEvent('passport-material-active', { detail: id }));
    setPlaying(!document.hidden);
    const timer = window.setTimeout(
      () => setPlaying(false),
      Math.max(visual.reveal.duration_ms, visual.reveal.lifetime_ms, visual.material.duration_ms)
    );
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('passport-material-active', stop);
      motion.removeEventListener('change', reduce);
      document.removeEventListener('visibilitychange', hide);
      observer?.disconnect();
    };
  }, [id, quiet, face, replay, visual]);
  useEffect(() => {
    const again = () => setReplay((n) => n + 1);
    window.addEventListener('passport-preview-replay', again);
    return () => window.removeEventListener('passport-preview-replay', again);
  }, []);
  if (!visual || visual.schema_version !== 1) return null;
  const r = visual.reveal;
  const count = playing ? Math.min(80, Math.floor(r.particles)) : 0;
  return (
    <div
      ref={element}
      className={`pe-material pe-stock-${visual.material.stock} ${playing ? 'pe-material-playing' : ''} pe-reveal-${r.preset}`}
      aria-hidden="true"
      style={
        {
          '--pe-life': `${r.lifetime_ms}ms`,
          '--pe-tint': r.tint === 'orange' ? '#ff9750' : '#d8dee8',
        } as React.CSSProperties
      }
    >
      <div className="pe-material-coat" />
      <div className="pe-material-shine" />
      {Array.from({ length: count }, (_, i) => {
        const seed = ((r.seed + i * 16807) % 2147483647) / 2147483647;
        const angle = (((i * 137.5 + r.seed) % 360) * Math.PI) / 180;
        const radius = 20 + ((i % 10) / 10) * r.spread;
        return (
          <i
            key={`${replay}-${i}`}
            className="pe-particle"
            style={
              {
                left: `${10 + ((i * 37 + r.seed) % 80)}%`,
                top: `${15 + ((i * 19 + r.seed) % 65)}%`,
                '--pe-dx': `${Math.cos(angle) * radius + r.drift * 50}px`,
                '--pe-dy': `${Math.sin(angle) * radius * r.speed}px`,
                animationDelay: `${seed * 100}ms`,
              } as React.CSSProperties
            }
          />
        );
      })}
    </div>
  );
}
